import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWallet } from './WalletContext';
import { useToast } from './hooks/useToast';
import './GameSelection.css';

function StarlineGameSelection({ gameName, onBack, gameId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { walletBalance, updateWalletBalance } = useWallet();
  const toast = useToast();
  const [user, setUser] = useState(null);

  const isMarketClosed = () => {
    const closeTimeStr = location.state?.closeTime;
    if (!closeTimeStr) return false;
    const now = new Date();
    const kolkataTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const currentTimeInMinutes = kolkataTime.getHours() * 60 + kolkataTime.getMinutes();
    const [ch, cm] = closeTimeStr.split(':').map(Number);
    return currentTimeInMinutes >= ch * 60 + cm - 5;
  };

  // Load user data from localStorage and fetch wallet balance
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        
        // Fetch wallet balance using context
        if (parsedUser?.id) {
          updateWalletBalance(parsedUser.id);
        }
      } catch (err) {
        console.error('Error parsing user data:', err);
      }
    }
  }, [updateWalletBalance]);

  // Define starline game options - only Single Digit, Single Panna, Double Panna, Triple Panna
  const starlineGameOptions = [
    {
      id: 1,
      title: 'Single Digit',
      icon: (
        <svg width="60" height="60" viewBox="0 0 60 60" fill="white">
          <rect x="15" y="15" width="30" height="30" rx="2" stroke="white" strokeWidth="3" fill="none"/>
          <circle cx="30" cy="30" r="4" fill="white"/>
        </svg>
      ),
      color: '#5B8DEE'
    },
    {
      id: 2,
      title: 'Single Panna',
      icon: (
        <svg width="60" height="60" viewBox="0 0 60 60" fill="white">
          <rect x="20" y="15" width="20" height="30" rx="2" fill="white" fillOpacity="0.3"/>
          <path d="M25 18L25 42M30 18L30 42M35 18L35 42" stroke="white" strokeWidth="2"/>
          <circle cx="30" cy="30" r="3" fill="white"/>
        </svg>
      ),
      color: '#2ECC71'
    },
    {
      id: 3,
      title: 'Double Panna',
      icon: (
        <svg width="60" height="60" viewBox="0 0 60 60" fill="white">
          <rect x="18" y="15" width="20" height="28" rx="2" fill="white" fillOpacity="0.2" stroke="white" strokeWidth="2"/>
          <circle cx="24" cy="25" r="2.5" fill="white"/>
          <circle cx="32" cy="35" r="2.5" fill="white"/>
          <circle cx="28" cy="28" r="1.5" fill="white"/>
        </svg>
      ),
      color: '#9B59B6'
    },
    {
      id: 4,
      title: 'Triple Panna',
      icon: (
        <svg width="60" height="60" viewBox="0 0 60 60" fill="white">
          <rect x="18" y="15" width="24" height="30" rx="2" fill="white" fillOpacity="0.2" stroke="white" strokeWidth="2"/>
          <circle cx="23" cy="23" r="2" fill="white"/>
          <circle cx="30" cy="30" r="2" fill="white"/>
          <circle cx="37" cy="37" r="2" fill="white"/>
          <circle cx="23" cy="37" r="1.5" fill="white"/>
          <circle cx="37" cy="23" r="1.5" fill="white"/>
        </svg>
      ),
      color: '#E74C3C'
    }
  ];

  // Determine the correct route path based on option ID
  const getGameRoutePath = (optionId) => {
    const routeMap = {
      1: `/starline-single-digit-game/${gameId}`,
      2: `/starline-single-panna-game/${gameId}`,
      3: `/starline-double-panna-game/${gameId}`,
      4: `/starline-triple-panna-game/${gameId}`
    };
    return routeMap[optionId] || `/starline-single-digit-game/${gameId}`;
  };

  // Handle game option click
  const handleGameOptionClick = (option) => {
    if (isMarketClosed()) {
      toast.error('Market Closed');
      onBack();
      return;
    }
    navigate(getGameRoutePath(option.id), {
      state: {
        gameName: location.state?.gameName || gameName,
        openTime: location.state?.openTime,
        closeTime: location.state?.closeTime,
        gameTypeId: option.id,
        gameId: gameId
      }
    });
  };

  return (
    <div className="game-selection">
      {/* Header with Back Button */}
      <header className="game-selection-header">
        <button className="back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="game-selection-title">{location.state?.gameName || gameName || 'SELECT GAME'}</div>
        <button className="wallet-btn" onClick={() => navigate("/funds")}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="6" width="20" height="14" rx="2" stroke="white" strokeWidth="2"/>
            <path d="M2 10H22" stroke="white" strokeWidth="2"/>
            <circle cx="18" cy="15" r="1.5" fill="white"/>
          </svg>
          <span className="wallet-balance">₹ {walletBalance}</span>
        </button>
      </header>

      {/* Game Options Grid */}
      <div className="game-options-container">
        <div className="game-options-grid">
          {starlineGameOptions.map((option) => (
            <button
              key={option.id}
              className="game-option-card"
              onClick={() => handleGameOptionClick(option)}
              style={{ cursor: 'pointer' }}
            >
              <div className="game-option-icon" style={{ backgroundColor: option.color }}>
                {option.icon}
                <div className="icon-underline" style={{ backgroundColor: option.color }}></div>
              </div>
              <div className="game-option-title">{option.title}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default StarlineGameSelection;
